

import React, { useState, useEffect, useRef } from 'react';
import { Modal } from './ui/Modal';
import { Attachment } from '../types';
import { DownloadIcon } from './icons/DownloadIcon';
import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist/build/pdf.mjs';

// This is a requirement for pdf.js to work in most bundler environments.
// Point it to a reliable CDN copy of the worker file.
GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.4.168/pdf.worker.mjs';

const dataURIToUint8Array = (dataURI: string): Uint8Array | null => {
  try {
    const base64 = dataURI.split(',')[1];
    if (!base64) throw new Error("Base64 string not found in data URI");
    const raw = window.atob(base64);
    const rawLength = raw.length;
    const array = new Uint8Array(new ArrayBuffer(rawLength));

    for (let i = 0; i < rawLength; i++) {
      array[i] = raw.charCodeAt(i);
    }
    return array;
  } catch (error) {
    console.error("Failed to convert data URI to Uint8Array:", error);
    return null;
  }
};


export const AttachmentViewerModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  attachment: Attachment | null;
}> = ({ isOpen, onClose, attachment }) => {
  const canvasContainerRef = useRef<HTMLDivElement>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  useEffect(() => {
    const container = canvasContainerRef.current;
    if (container) {
        container.innerHTML = ''; // Clean up previous canvases on re-render
    }
    setPdfError(null);

    if (isOpen && attachment?.type === 'application/pdf' && attachment.url) {
        const renderPdf = async () => {
            setPdfLoading(true);
            try {
                const pdfData = dataURIToUint8Array(attachment.url);
                if (!pdfData) {
                    throw new Error("Could not convert PDF data.");
                }

                const loadingTask = getDocument({ data: pdfData });
                const pdf = await loadingTask.promise;

                for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
                    const page = await pdf.getPage(pageNum);
                    const viewport = page.getViewport({ scale: 1.5 });
                    
                    const canvas = document.createElement('canvas');
                    canvas.className = 'mb-4 shadow-lg rounded-sm';
                    const context = canvas.getContext('2d');
                    if (!context) {
                        throw new Error("Could not get canvas context.");
                    }
                    canvas.height = viewport.height;
                    canvas.width = viewport.width;

                    container?.appendChild(canvas);

                    const renderContext = {
                        canvasContext: context,
                        viewport: viewport
                    };
                    await page.render(renderContext).promise;
                }
            } catch (e: any) {
                console.error('Error rendering PDF:', e);
                setPdfError(e.message || 'Falha ao renderizar o PDF. O arquivo pode estar corrompido.');
            } finally {
                setPdfLoading(false);
            }
        };

        renderPdf();
    }
    // Cleanup function not strictly necessary due to innerHTML reset, but good practice
    return () => {
        if (container) {
            container.innerHTML = '';
        }
    }
  }, [isOpen, attachment]);

  if (!isOpen || !attachment) {
    return null;
  }

  const { name, url, type } = attachment;

  const renderContent = () => {
    if (type.startsWith('image/')) {
      return <img src={url} alt={name} className="max-w-full max-h-full object-contain" />;
    }
    
    if (type === 'application/pdf') {
      return (
        <div ref={canvasContainerRef} className="w-full h-full overflow-y-auto bg-gray-500 dark:bg-dark-tertiary p-4 flex flex-col items-center">
            {pdfLoading && <p className="text-white text-center text-lg mt-10">Carregando PDF...</p>}
            {pdfError && (
                <div className="text-red-300 bg-red-900/50 p-4 rounded-md text-center mt-10">
                    <p className="font-bold">Erro ao exibir o PDF</p>
                    <p className="text-sm mt-1">{pdfError}</p>
                    <a 
                        href={url} 
                        download={name} 
                        className="text-brand-lime hover:underline mt-4 inline-flex items-center gap-2 bg-dark-surface p-2 rounded-lg transition-colors hover:bg-dark-secondary"
                    >
                        <DownloadIcon />
                        Baixar o arquivo
                    </a>
                </div>
            )}
            {/* Canvases are appended here via the useEffect hook */}
        </div>
      );
    }
    
    // Default fallback for other unsupported types
    return (
      <div className="text-white p-4 text-center flex flex-col items-center justify-center h-full">
        <p>Visualização não suportada para o tipo de arquivo: {type}.</p>
        <a 
          href={url} 
          download={name} 
          className="text-brand-lime hover:underline mt-4 inline-flex items-center gap-2 bg-dark-surface p-3 rounded-lg transition-colors hover:bg-dark-tertiary"
        >
          <DownloadIcon />
          Baixar o arquivo
        </a>
      </div>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={name} size="full">
      <div className="w-full h-full bg-dark-bg flex flex-col items-center justify-center">
        {renderContent()}
      </div>
    </Modal>
  );
};